import type { PropsWithChildren, ReactNode } from "react";
import type { AudienceRow, MediaKitContent, Metric } from "./types";

/** ISO 3166-1 alpha-2 for lipis/flag-icons (`fi fi-xx`) */
const COUNTRY_ISO: Record<string, string> = {
  ar: "ar",
  argentina: "ar",
  mx: "mx",
  mexico: "mx",
  méxico: "mx",
  es: "es",
  spain: "es",
  españa: "es",
  espana: "es",
  cl: "cl",
  chile: "cl",
  co: "co",
  colombia: "co",
  pe: "pe",
  peru: "pe",
  perú: "pe",
  uy: "uy",
  uruguay: "uy",
  br: "br",
  brasil: "br",
  brazil: "br",
  us: "us",
  usa: "us",
  "united states": "us",
  estadosunidos: "us",
  "estados unidos": "us",
};

export function resolveCountryIso2(row: Pick<AudienceRow, "id" | "label">): string | null {
  const byId = COUNTRY_ISO[row.id?.trim().toLowerCase() ?? ""];
  if (byId) return byId;
  const byLabel = COUNTRY_ISO[row.label?.trim().toLowerCase() ?? ""];
  if (byLabel) return byLabel;
  // Accept raw 2-letter codes as id
  const raw = row.id?.trim().toLowerCase() ?? "";
  if (/^[a-z]{2}$/.test(raw)) return raw;
  return null;
}

export function CountryFlag({
  iso2,
  title,
}: {
  iso2: string;
  title?: string;
}) {
  const code = iso2.toLowerCase();
  return (
    <span
      className={`fi fi-${code} fis mk-flag`}
      title={title ?? code.toUpperCase()}
      role="img"
      aria-label={title ? `Bandera de ${title}` : `Bandera ${code.toUpperCase()}`}
    />
  );
}

export function Brand({ brand }: { brand: MediaKitContent["brand"] }) {
  return (
    <div className="mk-brand" aria-label={`${brand.name} ${brand.descriptor}`}>
      <strong>{brand.name}</strong>
      <span>{brand.descriptor}</span>
    </div>
  );
}

export function Kicker({ children }: PropsWithChildren) {
  return <p className="mk-kicker">{children}</p>;
}

export function PageFooter({
  page,
  name = "Delfu Ocampo",
  total = "08",
}: {
  page: string;
  name?: string;
  total?: string;
}) {
  return (
    <footer className="mk-page-footer">
      <span>{name}</span>
      <span>
        {page} · {total}
      </span>
    </footer>
  );
}

export function Section({
  id,
  page,
  children,
  className = "",
  footerName,
}: PropsWithChildren<{
  id: string;
  page: string;
  className?: string;
  footerName?: string;
}>) {
  return (
    <section id={id} className={`mk-page ${className}`.trim()}>
      {children}
      <PageFooter page={page} name={footerName} />
    </section>
  );
}

export function MetricGrid({
  items,
  compact = false,
}: {
  items: Metric[];
  compact?: boolean;
}) {
  return (
    <div className={`mk-metric-grid ${compact ? "mk-metric-grid--compact" : ""}`}>
      {items.map((metric) => (
        <article className="mk-metric" key={metric.id}>
          <strong className={`mk-tone-${metric.tone ?? "green"}`}>{metric.value}</strong>
          <span>{metric.label}</span>
          {metric.hint ? <em className="mk-metric-hint">{metric.hint}</em> : null}
        </article>
      ))}
    </div>
  );
}

export function Bars({
  title,
  rows,
  showCountryFlags = false,
}: {
  title: string;
  rows: AudienceRow[];
  /** Renders lipis/flag-icons next to each country label */
  showCountryFlags?: boolean;
}) {
  return (
    <div className={`mk-chart-card${showCountryFlags ? " mk-chart-card--flags" : ""}`}>
      <h3>{title}</h3>
      <div className="mk-bars">
        {rows.map((row) => {
          const iso = showCountryFlags ? resolveCountryIso2(row) : null;
          return (
            <div className="mk-bar-row" key={row.id || row.label}>
              <span className="mk-bar-label">
                {iso ? <CountryFlag iso2={iso} title={row.label} /> : null}
                <span className="mk-bar-label-text">{row.label}</span>
              </span>
              <div className="mk-bar-track">
                <i
                  className={`mk-bar-fill mk-tone-bg-${row.tone ?? "green"}`}
                  style={{ width: `${Math.min(100, Math.max(0, row.value))}%` }}
                />
              </div>
              <strong>{row.display}</strong>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Heading({
  children,
  accent,
}: {
  children: ReactNode;
  accent?: ReactNode;
}) {
  return (
    <h2 className="mk-display-heading">
      {children} {accent && <em>{accent}</em>}
    </h2>
  );
}
